import { NoraCheckboxInput, NoraTextAreaInput } from 'ethos-design-system';
import React, { useEffect, useState } from 'react';

import { FlexGrid, FlexRow, Spacer } from '../../../../../common/components/v2';
import { Reason } from '../../../../../common/constants/reasons';
import { Resource } from '../../../../../common/constants/resource';
import { MedExam } from '../../../../tasks/types/medExam';
import { ReasonsChoicePanel } from '../../ReasonsChoicePanel';
import { RequiredFieldErrorBar } from '../../RequiredFieldErrorBar';
import { RequiredFieldTitle } from '../../RequiredFieldTitle';
import styles from '../../styles.module.scss';

export enum LabTypes {
  CEA = 'CEA',
  PROBNP = 'ProBNP',
  PSA = 'PSA',
  HISTORICAL_LAB = 'Historical Lab',
}

export const MedExamLayout = ({
  medExam,
  setMedExam,
  isExpanded,
  reasons,
  medExamError,
}: {
  medExam: MedExam;
  setMedExam: React.Dispatch<React.SetStateAction<MedExam>>;
  isExpanded: boolean;
  reasons: Reason[];
  medExamError: boolean;
}) => {
  // Initial state should reflect: Labs and PhysMeas checked, all other unchecked
  // If Labs and PhysMeas were intentionally un-checked, the state should reflect that as well
  const [labSelected, setLabSelected] = useState(
    medExam.labs !== undefined ? medExam.labs : true,
  );
  const [physicalMeasurementSelected, setPhysicalMeasurementSelected] =
    useState(
      medExam.physicalMeasurements !== undefined
        ? medExam.physicalMeasurements
        : true,
    );
  const [ekgSelected, setEkgSelected] = useState(medExam.ekg || false);
  const [ceaSelected, setCeaSelected] = useState(false);
  const [probnpSelected, setProbnpSelected] = useState(false);
  const [psaSelected, setPsaSelected] = useState(false);
  const [historicalLabSelected, setHistoricalLabSelected] = useState(false);
  const [reasonDescription, setReasonDescription] = useState(
    medExam.reasonDescription || '',
  );
  const [selectedReasons, setSelectedReasons] = useState(
    medExam.reason || ([] as string[]),
  );

  // If MedExam has lab options, update the default
  useEffect(() => {
    if (medExam.labsOptions) {
      const labsOptions = medExam.labsOptions;
      if (labsOptions.indexOf(LabTypes.CEA) !== -1) {
        setCeaSelected(true);
      }
      if (labsOptions.indexOf(LabTypes.PROBNP) !== -1) {
        setProbnpSelected(true);
      }
      if (labsOptions.indexOf(LabTypes.PSA) !== -1) {
        setPsaSelected(true);
      }
      if (labsOptions.indexOf(LabTypes.HISTORICAL_LAB) !== -1) {
        setHistoricalLabSelected(true);
      }
    }
  }, []);

  const addToCart = (medicalExam: MedExam) => {
    medicalExam.physicalMeasurements = physicalMeasurementSelected;
    medicalExam.ekg = ekgSelected;
    medicalExam.reasonDescription = reasonDescription;
    medicalExam.reason = selectedReasons;
    medicalExam.labs = labSelected;
    const labsOptions: string[] = [];
    if (ceaSelected) {
      labsOptions.push(LabTypes.CEA);
    }
    if (probnpSelected) {
      labsOptions.push(LabTypes.PROBNP);
    }
    if (psaSelected) {
      labsOptions.push(LabTypes.PSA);
    }
    if (historicalLabSelected) {
      labsOptions.push(LabTypes.HISTORICAL_LAB);
    }
    medicalExam.labsOptions = labsOptions;
  };

  const resetState = () => {
    setLabSelected(true);
    setCeaSelected(false);
    setProbnpSelected(false);
    setPsaSelected(false);
    setHistoricalLabSelected(false);
    setPhysicalMeasurementSelected(true);
    setEkgSelected(false);
    setReasonDescription('');
    setSelectedReasons([] as string[]);
  };

  useEffect(() => {
    const newMedExam = {} as MedExam;
    if (isExpanded) {
      addToCart(newMedExam);
    } else {
      resetState();
    }
    setMedExam(newMedExam);
  }, [
    isExpanded,
    reasonDescription,
    labSelected,
    ceaSelected,
    probnpSelected,
    psaSelected,
    historicalLabSelected,
    physicalMeasurementSelected,
    ekgSelected,
    selectedReasons.length,
  ]);

  return (
    <FlexGrid>
      {medExamError && (
        <>
          <RequiredFieldErrorBar />
          <Spacer height={8} />
        </>
      )}
      <RequiredFieldTitle title={Resource.TYPE} />
      <Spacer height={8} />
      <FlexRow>
        <NoraCheckboxInput
          name="labs"
          data-tid="labs-checkbox"
          checked={labSelected}
          validator={val => {
            const bool = !!val;
            setLabSelected(bool);
            // Unchecking Labs unchecks all children
            // Checking Labs does nothing to children as a user can order just labs
            if (!bool) {
              setCeaSelected(bool);
              setProbnpSelected(bool);
              setPsaSelected(bool);
              setHistoricalLabSelected(bool);
            }
            return '';
          }}
        >
          <div className={styles.ChecklistOption}>Labs</div>
          <NoraCheckboxInput
            name="cea"
            data-tid="cea-checkbox"
            checked={ceaSelected}
            validator={val => {
              const bool = !!val;
              setCeaSelected(bool);
              if (bool) {
                setLabSelected(true);
              }
              return '';
            }}
          >
            <div className={styles.ChecklistOption}>CEA</div>
          </NoraCheckboxInput>
          <NoraCheckboxInput
            name="probnp"
            data-tid="probnp-checkbox"
            checked={probnpSelected}
            validator={val => {
              const bool = !!val;
              setProbnpSelected(bool);
              if (bool) {
                setLabSelected(true);
              }
              return '';
            }}
          >
            <div className={styles.ChecklistOption}>ProBNP</div>
          </NoraCheckboxInput>
          <NoraCheckboxInput
            name="psa"
            data-tid="psa-checkbox"
            checked={psaSelected}
            validator={val => {
              const bool = !!val;
              setPsaSelected(bool);
              if (bool) {
                setLabSelected(true);
              }
              return '';
            }}
          >
            <div className={styles.ChecklistOption}>PSA</div>
          </NoraCheckboxInput>
        </NoraCheckboxInput>
      </FlexRow>
      <FlexRow>
        <NoraCheckboxInput
          name="phys-meas"
          data-tid="phys-meas-checkbox"
          checked={physicalMeasurementSelected}
          validator={val => {
            const bool = !!val;
            setPhysicalMeasurementSelected(bool);
            return '';
          }}
        >
          <div className={styles.ChecklistOption}>Physical Measurements</div>
        </NoraCheckboxInput>
      </FlexRow>
      <FlexRow>
        <NoraCheckboxInput
          name="ekg"
          data-tid="ekg-checkbox"
          checked={ekgSelected}
          validator={val => {
            const bool = !!val;
            setEkgSelected(bool);
            return '';
          }}
        >
          <div className={styles.ChecklistOption}>EKG</div>
        </NoraCheckboxInput>
      </FlexRow>
      <Spacer height={16} />
      <FlexRow>
        <RequiredFieldTitle title={Resource.ORDER_REASON} />
        <ReasonsChoicePanel
          reasons={reasons}
          selectedReasons={selectedReasons}
          selectReasonsCallback={setSelectedReasons}
        />
        <NoraTextAreaInput
          name="med-exam-reason"
          data-tid="med-exam-reason-textarea"
          placeholder="Describe the reason"
          textClassName={styles.TextareaInput}
          onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>) => {
            setReasonDescription(ev.currentTarget.value);
          }}
          value={reasonDescription}
        />
      </FlexRow>
    </FlexGrid>
  );
};
